import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "./Reveal";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "Do I need to provide the footage?",
    a: "If the format needs you on camera, you film — we handle everything after. Many formats don't need new footage from you at all.",
  },
  {
    q: "How much involvement do you need from me?",
    a: "As little as you want. Most clients spend under an hour a week: a short strategy call and final approvals where their input actually matters.",
  },
  {
    q: "Who actually does the work?",
    a: "A managed team of specialists — strategists, writers, editors, and designers — sourced and managed by us. You never have to manage a freelancer.",
  },
  {
    q: "How do you choose editors and designers?",
    a: "We test them on real briefs before they ever touch your content, and we keep the ones who consistently perform.",
  },
  {
    q: "How often will I need to approve things?",
    a: "Only where it counts: direction, scripts, and final packaging. Everything else keeps moving without you.",
  },
  {
    q: "Can you work with my existing team?",
    a: "Yes. We can run the entire content system, or plug into your team and handle the pieces that are missing.",
  },
  {
    q: "What platforms do you handle?",
    a: "YouTube first, plus the short-form platforms that fit your audience — repurposed from the same system, not extra work for you.",
  },
  {
    q: "How do you measure success?",
    a: "Against goals we agree on upfront: the right viewers, watch time, and what the content brings back to the business.",
  },
  {
    q: "How long before we see results?",
    a: "You'll see clearer direction and better output in the first month. Compounding growth builds over months of consistent publishing — we optimize as we go.",
  },
];

const FaqItem = ({ item, i, open, onToggle }) => (
  <div className="border-t border-black/[0.08]" data-testid={`faq-item-${i + 1}`}>
    <button
      onClick={onToggle}
      data-testid={`faq-question-${i + 1}`}
      className="flex w-full items-center justify-between gap-6 py-6 text-left group"
      aria-expanded={open}
    >
      <span className="font-display text-lg sm:text-xl font-semibold tracking-[-0.01em] text-[#1d1d1f]">
        {item.q}
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
          open ? "border-[#1d1d1f] bg-[#1d1d1f] text-white" : "border-black/15 text-[#86868b] group-hover:border-black/40"
        }`}
      >
        <Plus className="h-4 w-4" strokeWidth={1.75} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-7 pr-16 text-[15px] sm:text-base text-[#6e6e73] leading-relaxed max-w-2xl">
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-[#f5f5f7] py-28 sm:py-40" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <span className="eyebrow text-[#86868b]">FAQ</span>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] text-[#1d1d1f]">
            Questions, answered.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <div>
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                item={f}
                i={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
            <div className="border-t border-black/[0.08]" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
